import React from "react";

import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import { cls } from "app/utils";

import styles from "./CoachAvatar.module.scss";
import { coachInitials } from "./coachMeta";

interface Props {
  coach: CoachProfileModel;
  /** The surface's own avatar box — its size, its radius, its crop. */
  className: string;
  /**
   * The letters' size, which the box cannot tell us: a 100px card and a 32px
   * column head take the same component. Roughly a third of the box.
   */
  fontSize: number;
}

/**
 * A coach's photo, or their initials where they have none.
 *
 * Every screen in the booking flow drew nothing at all for a coach without a
 * photo — the row simply lost its avatar and the names stopped lining up — or, on
 * the coach cards, an empty grey square. The initials keep the shape and say who
 * it belongs to.
 *
 * The box belongs to the caller. Only what makes it a placeholder is here, so a
 * 100px card and a 32px column head stay their own sizes.
 */
const CoachAvatar: React.FC<Props> = ({ coach, className, fontSize }) => {
  if (coach?.profilePicture) {
    return <img className={className} src={coach.profilePicture} alt="" />;
  }

  return (
    <div
      className={cls(className, styles.initials)}
      style={{ fontSize }}
      // The name is beside it on every screen that draws one, so the letters are
      // a second telling rather than the only one.
      aria-hidden
      data-testid="coach-initials"
    >
      {coachInitials(coach)}
    </div>
  );
};

export default CoachAvatar;
